import React, { useState, useEffect } from 'react';
import { MarketNews, EconomicEvent, MarketOverview, TrendingCoin } from '../types/marketData';
import { newsService } from '../services/newsService';

interface NewsPanelProps {
  onNewsClick?: (news: MarketNews) => void;
  onClose?: () => void;
}

const NewsPanel = ({ onNewsClick, onClose }: NewsPanelProps) => {
  const [activeTab, setActiveTab] = useState<'news' | 'trending' | 'events' | 'overview'>('news');
  const [news, setNews] = useState<MarketNews[]>([]);
  const [trending, setTrending] = useState<TrendingCoin[]>([]);
  const [events, setEvents] = useState<EconomicEvent[]>([]);
  const [overview, setOverview] = useState<MarketOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [fearGreedIndex, setFearGreedIndex] = useState(50);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5 * 60 * 1000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [newsData, trendingData, eventsData, overviewData, fngIndex] = await Promise.all([
        newsService.getAllNews(),
        newsService.fetchTrendingCoins(),
        newsService.fetchEconomicEvents(),
        newsService.fetchMarketOverview(),
        newsService.fetchFearGreedIndex()
      ]);

      setNews(newsData);
      setTrending(trendingData);
      setEvents(eventsData);
      setOverview(overviewData);
      setFearGreedIndex(fngIndex);
    } catch (error) {
      console.warn('Failed to load market data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const formatPrice = (price: number) => {
    if (price >= 1) return `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    return `$${price.toFixed(6)}`;
  };

  const formatMarketCap = (cap: number) => {
    if (cap >= 1e12) return `$${(cap / 1e12).toFixed(2)}T`;
    if (cap >= 1e9) return `$${(cap / 1e9).toFixed(2)}B`;
    if (cap >= 1e6) return `$${(cap / 1e6).toFixed(2)}M`;
    return `$${cap.toLocaleString()}`;
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return '#10b981';
      case 'negative': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return '#dc2626';
      case 'medium': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getFearGreedColor = (index: number) => {
    if (index >= 75) return '#ef4444'; // Extreme Greed
    if (index >= 55) return '#f59e0b'; // Greed
    if (index >= 45) return '#10b981'; // Neutral
    if (index >= 25) return '#f59e0b'; // Fear
    return '#ef4444'; // Extreme Fear
  };

  const getFearGreedText = (index: number) => {
    if (index >= 75) return 'Extreme Greed';
    if (index >= 55) return 'Greed';
    if (index >= 45) return 'Neutral';
    if (index >= 25) return 'Fear';
    return 'Extreme Fear';
  };

  return (
    <div className="news-panel">
      <div className="news-header">
        <h3>Market Data & News</h3>
        <div className="news-header-actions">
          <button 
            className="refresh-btn"
            onClick={loadData}
            disabled={loading}
          >
            {loading ? '⏳' : '🔄'}
          </button>
          {onClose && (
            <button 
              className="close-btn"
              onClick={onClose}
              title="Close News Panel"
            >
              ✖
            </button>
          )}
        </div>
      </div>

      <div className="news-tabs">
        <button 
          className={`tab ${activeTab === 'news' ? 'active' : ''}`}
          onClick={() => setActiveTab('news')}
        >
          📰 News
        </button>
        <button 
          className={`tab ${activeTab === 'trending' ? 'active' : ''}`}
          onClick={() => setActiveTab('trending')}
        >
          🔥 Trending
        </button>
        <button 
          className={`tab ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          📅 Events
        </button>
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
      </div>

      <div className="news-content">
        {activeTab === 'news' && (
          <div className="news-list">
            {news.length === 0 ? (
              <div className="no-data">Loading news...</div>
            ) : (
              news.slice(0, 10).map((item) => (
                <div 
                  key={item.id} 
                  className="news-item"
                  onClick={() => onNewsClick?.(item)}
                >
                  <div className="news-item-header">
                    <div className="news-category">
                      <span className={`category-badge ${item.category}`}>
                        {item.category.toUpperCase()}
                      </span>
                    </div>
                    <div className="news-time">{formatTime(item.publishedAt)}</div>
                  </div>
                  
                  <h4 className="news-title">{item.title}</h4>
                  
                  <div className="news-meta">
                    <span 
                      className="sentiment"
                      style={{ color: getSentimentColor(item.sentiment) }}
                    >
                      {item.sentiment}
                    </span>
                    <span 
                      className="impact"
                      style={{ color: getImpactColor(item.impact) }}
                    >
                      {item.impact} impact
                    </span>
                    <span className="source">{item.source}</span>
                  </div>
                  
                  {item.tags.length > 0 && (
                    <div className="news-tags">
                      {item.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="tag">#{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'trending' && (
          <div className="trending-list">
            {trending.length === 0 ? (
              <div className="no-data">Loading trending coins...</div>
            ) : (
              trending.slice(0, 10).map((coin) => (
                <div key={coin.id} className="trending-item">
                  <div className="coin-info">
                    <div className="coin-symbol">{coin.symbol}</div>
                    <div className="coin-name">{coin.name}</div>
                  </div>
                  <div className="coin-price">{formatPrice(coin.price)}</div>
                  <div className={`coin-change ${coin.change24h >= 0 ? 'positive' : 'negative'}`}>
                    {coin.change24h >= 0 ? '+' : ''}{coin.change24h.toFixed(2)}%
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'events' && (
          <div className="events-list">
            {events.length === 0 ? (
              <div className="no-data">Loading economic events...</div>
            ) : (
              events.map((event) => (
                <div key={event.id} className="event-item">
                  <div className="event-header">
                    <div className="event-title">{event.title}</div>
                    <div className="event-time">{formatTime(event.time)}</div>
                  </div>
                  <div className="event-details">
                    <span className="event-country">{event.country}</span>
                    <span className={`event-importance ${event.importance}`}>
                      {event.importance} importance
                    </span>
                  </div>
                  {event.forecast && (
                    <div className="event-forecast">
                      Forecast: {event.forecast} | Previous: {event.previous}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'overview' && overview && (
          <div className="overview-content">
            <div className="overview-stats">
              <div className="stat-item">
                <div className="stat-label">Total Market Cap</div>
                <div className="stat-value">{formatMarketCap(overview.totalMarketCap)}</div>
              </div>
              <div className="stat-item">
                <div className="stat-label">24h Volume</div>
                <div className="stat-value">{formatMarketCap(overview.totalVolume)}</div>
              </div>
              <div className="stat-item">
                <div className="stat-label">BTC Dominance</div>
                <div className="stat-value">{overview.bitcoinDominance.toFixed(1)}%</div>
              </div>
              <div className="stat-item">
                <div className="stat-label">ETH Dominance</div>
                <div className="stat-value">{overview.ethereumDominance.toFixed(1)}%</div>
              </div>
            </div>
            
            <div className="fear-greed-index">
              <div className="fng-label">Fear & Greed Index</div>
              <div className="fng-container">
                <div 
                  className="fng-bar"
                  style={{ 
                    width: `${fearGreedIndex}%`,
                    backgroundColor: getFearGreedColor(fearGreedIndex)
                  }}
                />
                <div className="fng-value">{fearGreedIndex}</div>
              </div>
              <div 
                className="fng-text"
                style={{ color: getFearGreedColor(fearGreedIndex) }}
              >
                {getFearGreedText(fearGreedIndex)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsPanel;
